import React from 'react';
import { motion } from 'framer-motion';
import { VaultKeTheme } from '../utils/theme';

const Footer: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    footer: {
      backgroundColor: VaultKeTheme.colors.surface,
      padding: `${VaultKeTheme.spacing.xxl} clamp(${VaultKeTheme.spacing.md}, 5vw, ${VaultKeTheme.spacing.xl}) ${VaultKeTheme.spacing.lg}`,
      borderTop: `1px solid ${VaultKeTheme.colors.border}`,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: `clamp(${VaultKeTheme.spacing.md}, 4vw, ${VaultKeTheme.spacing.xl})`,
      marginBottom: VaultKeTheme.spacing.xl,
    },
    section: {
      color: VaultKeTheme.colors.text,
    },
    title: {
      ...VaultKeTheme.typography.h5,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
      fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
    },
    link: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      textDecoration: 'none',
      display: 'block',
      marginBottom: VaultKeTheme.spacing.sm,
      transition: 'color 0.2s ease',
      cursor: 'pointer',
      fontSize: 'clamp(0.85rem, 2vw, 0.95rem)',
    },
    bottom: {
      borderTop: `1px solid ${VaultKeTheme.colors.divider}`,
      paddingTop: VaultKeTheme.spacing.lg,
      textAlign: 'center',
    },
    copyright: {
      ...VaultKeTheme.typography.caption,
      color: VaultKeTheme.colors.textTertiary,
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <motion.footer
      style={styles.footer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={containerVariants}
    >
      <div style={styles.container}>
        <motion.div
          style={{
            ...styles.grid,
            justifyContent: 'center',
            alignItems: 'start'
          }}
          variants={containerVariants}
        >
          <motion.div style={{ ...styles.section, textAlign: 'center' }} variants={itemVariants}>
            <motion.h3 style={styles.title} variants={itemVariants}>VaultKe</motion.h3>
            <motion.p
              style={{
                ...VaultKeTheme.typography.body2,
                color: VaultKeTheme.colors.textSecondary,
                marginBottom: VaultKeTheme.spacing.lg,
                textAlign: 'center',
                fontSize: 'clamp(0.85rem, 2vw, 1rem)',
                lineHeight: '1.6',
                maxWidth: '300px',
                margin: '0 auto'
              }}
              variants={itemVariants}
            >
              Empowering community savings groups with modern financial technology for secure and intelligent wealth management.
            </motion.p>
          </motion.div>

          <motion.div style={{ ...styles.section, textAlign: 'center' }} variants={itemVariants}>
            <motion.h4 style={styles.title} variants={itemVariants}>Product</motion.h4>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: VaultKeTheme.spacing.sm }}>
              <motion.div variants={itemVariants} whileHover={{ color: VaultKeTheme.colors.primary }}>
                <a style={styles.link} href="#features">Features</a>
              </motion.div>
              <motion.div variants={itemVariants} whileHover={{ color: VaultKeTheme.colors.primary }}>
                <a style={styles.link} href="/pricing">Pricing</a>
              </motion.div>
              <motion.div variants={itemVariants} whileHover={{ color: VaultKeTheme.colors.primary }}>
                <a style={styles.link} href="/security">Security</a>
              </motion.div>
            </div>
          </motion.div>

          <motion.div style={{ ...styles.section, textAlign: 'center' }} variants={itemVariants}>
            <motion.h4 style={styles.title} variants={itemVariants}>Company</motion.h4>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: VaultKeTheme.spacing.sm }}>
              <motion.div variants={itemVariants} whileHover={{ color: VaultKeTheme.colors.primary }}>
                <a style={styles.link} href="/about">About Us</a>
              </motion.div>
              <motion.div variants={itemVariants} whileHover={{ color: VaultKeTheme.colors.primary }}>
                <a style={styles.link} href="/careers">Careers</a>
              </motion.div>
              <motion.div variants={itemVariants} whileHover={{ color: VaultKeTheme.colors.primary }}>
                <a style={styles.link} href="/contact">Contact</a>
              </motion.div>
            </div>
          </motion.div>

          <motion.div style={{ ...styles.section, textAlign: 'center' }} variants={itemVariants}>
            <motion.h4 style={styles.title} variants={itemVariants}>Stay Updated</motion.h4>
            <motion.p
              style={{
                ...VaultKeTheme.typography.body2,
                color: VaultKeTheme.colors.textSecondary,
                marginBottom: VaultKeTheme.spacing.md,
                fontSize: 'clamp(0.8rem, 1.8vw, 0.95rem)',
                lineHeight: '1.6',
                textAlign: 'center',
                maxWidth: '250px',
                margin: '0 auto'
              }}
              variants={itemVariants}
            >
              Get the latest updates and financial insights delivered to your inbox.
            </motion.p>
            <motion.div
              style={{
                position: 'relative',
                marginBottom: VaultKeTheme.spacing.sm,
                maxWidth: '300px',
                margin: '0 auto'
              }}
              variants={itemVariants}
            >
              <input
                type="email"
                placeholder="Enter your email"
                style={{
                  ...VaultKeTheme.components.input,
                  padding: 'clamp(8px, 2vw, 12px) clamp(100px, 25vw, 120px) clamp(8px, 2vw, 12px) clamp(8px, 2vw, 12px)',
                  fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />
              <motion.button
                style={{
                  ...VaultKeTheme.components.button.primary,
                  position: 'absolute',
                  right: 'clamp(4px, 1vw, 8px)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
                  fontSize: 'clamp(0.7rem, 1.8vw, 0.8rem)',
                  whiteSpace: 'nowrap',
                  minWidth: 'clamp(80px, 20vw, 100px)',
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Subscribe
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div style={styles.bottom} variants={itemVariants}>
          <motion.p style={styles.copyright} variants={itemVariants}>
            © {new Date().getFullYear()} VaultKe. All rights reserved.
          </motion.p>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;